import { useMemo, useRef, useState } from 'react';
import { useLoaderData } from 'react-router';

import type { ContactLoaderData } from '@modules/professional/type/contact-page-types';
import { useProfessionalInitialRouteData } from '@modules/professional/hook/use-professional-initial-route-data';
import { useContactLocationMap } from '@modules/professional/hook/use-contact-location-map';
import { getLinkedProfileLocations } from '@modules/professional/util/contact-page-helper';
import { LocationMap } from '@modules/professional/component/contact-tab/LocationMap';
import { EnquiryInline } from '~/modules/enquiry/component/enquiry-inline/EnquiryInline';
import { useEnquiryVisibility } from '~/modules/enquiry/hooks/use-enquiry-visibility';
import { SpecifierLoginPrompt } from '~/modules/enquiry/component/specifier-login-prompt/SpecifierLoginPrompt';
import { CONTACT_ENQUIRY_ANCHOR } from './ProfessionalContactConnectSection';

/**
 * Contact tab location section (Figma 2193:11424) — the editorial map +
 * enquiry block. Left: the live Google map (reused {@link LocationMap}, real
 * pins) with a slim primary-location card overlaid; right: the inline enquiry
 * form in a light panel. The form is cosmetically matched to the Figma
 * (progress bar hidden, Send restyled as a solid pill) via CSS scoped to
 * `.contact-enquiry-form` so the shared EnquiryForm is unchanged elsewhere.
 */
export const ProfessionalContactLocationSection = () => {
    const loaderData = useLoaderData<ContactLoaderData>();
    const linkedProfiles =
        loaderData?.linkedProfiles as ContactLoaderData['linkedProfiles'];
    const professional = useProfessionalInitialRouteData();
    const { showEnquiryForm, showSpecifierLoginPrompt } =
        useEnquiryVisibility(professional);

    const listRef = useRef<HTMLDivElement>(null);

    const originalLocations = useMemo(
        () =>
            getLinkedProfileLocations(
                professional,
                linkedProfiles ?? {
                    linkedProfilesCount: 0,
                    linkedProfilesList: [],
                    linkedProfilesProductSharing: [],
                },
            ),
        [professional, linkedProfiles],
    );

    const {
        mapCenter,
        mapZoomNum,
        mapVariables,
        onMapMarkerHover,
        onMapMarkerClick,
        onLocationDetailClose,
        hoveredLocation,
        clickedLocation,
        filteredLocations,
        sortedLocations,
    } = useContactLocationMap(
        originalLocations,
        listRef,
        true,
        professional.Country,
        // Open zoomed out so all branches show; zoom in only when a pin is
        // clicked (matches the contact-tab feedback).
        false,
    );

    const [showPhone, setShowPhone] = useState(false);
    // The overlay card follows the clicked pin (prod behaviour), defaulting to
    // the head office. "Head Office" labels the primary; other branches show
    // their own name.
    const selected = clickedLocation ?? sortedLocations[0];
    const isHeadOffice = selected?.ID === sortedLocations[0]?.ID;
    const selectedPlace = selected
        ? [selected.Suburb, selected.City].filter(Boolean).join(', ')
        : '';

    return (
        <section className="w-full overflow-hidden bg-ground text-[#1f1f21] max-md:px-6 max-md:pt-8 max-md:pb-12 md:px-[3.125rem] md:pt-[1.5rem] md:pb-[7.5rem]">
            <div className="mx-auto w-full max-w-[113.75rem]">
                <div className="flex max-md:flex-col max-md:gap-8 md:gap-[8.2%]">
                    {/* Map + slim primary-location card. */}
                    <div className="bg-neutral-800 relative min-w-0 overflow-hidden rounded-tile max-md:h-[24rem] md:h-[42.8125rem] md:w-[57.9%]">
                        {sortedLocations.length > 0 ? (
                            <LocationMap
                                center={mapCenter}
                                zoom={mapZoomNum}
                                filteredLocations={filteredLocations}
                                onMapMarkerHover={onMapMarkerHover}
                                onMapMarkerClick={onMapMarkerClick}
                                onMapPopupClose={onLocationDetailClose}
                                hoveredLocation={hoveredLocation}
                                clickedLocation={clickedLocation}
                                linkedProfiles={linkedProfiles}
                                mapVariables={mapVariables}
                            />
                        ) : null}

                        {selected ? (
                            <div className="flex flex-col gap-[1.25rem] rounded-tile-card bg-[#fafafa] p-[1.5rem] text-[#141414] shadow-[0px_4px_14px_0px_rgba(0,0,0,0.1)] max-md:mt-4 md:absolute md:top-[1.75rem] md:left-[1.625rem] md:w-[27.375rem]">
                                <div>
                                    <p className="m-0 truncate font-sans leading-[1.6] text-[#141414] max-md:text-lg md:text-[1.375rem] md:tracking-[-0.041rem]">
                                        {professional.Title}
                                    </p>
                                    {selectedPlace ? (
                                        <p className="m-0 truncate font-sans leading-[1.6] text-[#aeaeae] max-md:text-sm md:text-base md:tracking-[-0.03rem]">
                                            {isHeadOffice
                                                ? `Head Office: ${selectedPlace}`
                                                : selectedPlace}
                                        </p>
                                    ) : null}
                                </div>
                                <div className="h-px w-full bg-black/10" />
                                <div className="flex flex-col gap-1 text-sm text-[#141414]">
                                    {selected.Address1 ? (
                                        <p className="m-0 font-sans leading-[1.43]">
                                            {selected.Address1}
                                        </p>
                                    ) : null}
                                    {selected.Phone ? (
                                        showPhone ? (
                                            <a
                                                href={`tel:${selected.Phone}`}
                                                className="font-sans leading-[1.43] underline"
                                            >
                                                {selected.Phone}
                                            </a>
                                        ) : (
                                            <button
                                                type="button"
                                                onClick={() =>
                                                    setShowPhone(true)
                                                }
                                                className="w-fit font-sans leading-[1.43] underline"
                                            >
                                                Show Phone number
                                            </button>
                                        )
                                    ) : null}
                                </div>
                                {selected.AllowsPickup ? (
                                    <div className="flex items-center gap-2 text-sm text-[#141414]">
                                        <svg
                                            viewBox="0 0 24 24"
                                            aria-hidden="true"
                                            className="size-3 shrink-0"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2.5"
                                        >
                                            <path d="M4 12l5 5L20 6" />
                                        </svg>
                                        Click &amp; Collect available
                                    </div>
                                ) : null}
                            </div>
                        ) : null}
                    </div>

                    {/* Inline enquiry form — stretches to the map's height so the
                    two columns align (Figma). */}
                    <div
                        id={CONTACT_ENQUIRY_ANCHOR}
                        // White, not an off-white: on the profile's #f5f5f7 the
                        // panel's edge was invisible and the form read as loose
                        // fields on the page rather than a box to fill in.
                        className="rounded-[0.75rem] bg-white max-md:w-full max-md:p-6 md:w-[28.55%] md:self-stretch md:px-[2.6875rem] md:py-[3.125rem]"
                    >
                        {showSpecifierLoginPrompt ? (
                            <SpecifierLoginPrompt
                                professionalName={professional.Title}
                                itemDescription="this professional"
                                showBottomBorder={false}
                            />
                        ) : showEnquiryForm ? (
                            <div className="contact-enquiry-form">
                                <EnquiryInline
                                    professional={professional}
                                    includeBranch
                                    // The revamp does not ask for a suburb —
                                    // the reader's home address is not where
                                    // the project is, and an attached BuildHub
                                    // project carries the real location.
                                    collectLocation={false}
                                    showCustomEnquiryText
                                    submitButtonText="Send"
                                    siteTreeID={professional.ID}
                                    itemType="professional"
                                    itemTitle={professional.Title}
                                    ctaLabel="Get in touch"
                                />
                            </div>
                        ) : null}
                    </div>
                </div>
            </div>
        </section>
    );
};
